import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import CustomSelect from "../../../../components/Select/Select";

const chartStyle = {
  background: "#fff",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 0 10px rgba(0,0,0,0.05)",
  flex: 1,
};

const labelStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "6px",
  fontSize: "14px",
};

const periodTypeList = [
  { name: "Current Period", value: "currentPeriod" },
  { name: "Prior Period", value: "priorPeriod" },
  { name: "Trend", value: "trend" },
];

export default function CaseMix() {
  const { caseMixPerVet } = useSelector((state) => state.doctorDashboard);
  const [selectedViewType, setSelectedViewType] = useState("week");
  const [filterValues, setFilterValues] = useState({
    period: null,
    periodType: periodTypeList?.[0],
    vet: null,
  });

  useEffect(() => {
    if (caseMixPerVet && !filterValues?.vet) {
      const reqvet = caseMixPerVet?.[selectedViewType]?.vets?.[0];
      const reqPeriod = Object.keys(reqvet?.periods || {})?.[0];
      if (reqvet) {
        setFilterValues({
          ...filterValues,
          vet: { name: reqvet?.doctor, value: reqvet?.doctor },
          period: { name: reqPeriod, value: reqPeriod },
        });
      }
    }
  }, [caseMixPerVet, selectedViewType]);

  if (!caseMixPerVet) return null;

  const breedColors = ["#004f9e", "#9ca9ff", "#dee6ff", "#c0d4ff", "#a5bfff"]; // 5 colors

  const getVetList = () => {
    return caseMixPerVet?.[selectedViewType]?.vets?.map((li) => {
      return { name: li?.doctor, value: li?.doctor };
    });
  };

  const getPeriodList = () => {
    const vetData = caseMixPerVet?.[selectedViewType]?.vets?.find(
      (li) =>
        li?.doctor?.toLowerCase() === filterValues?.vet?.value?.toLowerCase()
    );
    const reqList = Object.keys(vetData?.periods || {})?.map((li) => {
      return { name: li, value: li };
    });
    return reqList;
  };

  const reqData = (getWithPeriodOnly = false) => {
    const reqVetData = caseMixPerVet?.[selectedViewType]?.vets?.find(
      (li) =>
        li?.doctor?.toLowerCase() === filterValues?.vet?.value?.toLowerCase()
    );
    if (getWithPeriodOnly) {
      return reqVetData?.periods?.[filterValues?.period?.value];
    }
    return (
      reqVetData?.periods?.[filterValues?.period?.value]?.[
      filterValues?.periodType?.value
      ] ?? {}
    );
  };

  const getPriorPeriodData = () => {
    if (!filterValues?.period?.value) return {};
    const reqVetData = caseMixPerVet?.[selectedViewType]?.vets?.find(
      (li) =>
        li?.doctor?.toLowerCase() === filterValues?.vet?.value?.toLowerCase()
    );
    return (
      reqVetData?.periods?.[filterValues?.period?.value]?.["priorPeriod"] ?? {}
    );
  };

  const currentData = reqData();
  const priorData = getPriorPeriodData();
  const dataKeys = Object.keys(currentData)?.slice(0, -1) ?? [];

  const breedsChart = {
    title: "Case Mix per Vet",
    labels:
      dataKeys?.map((key) => key?.charAt(0)?.toUpperCase() + key?.slice(1)) ??
      [],
    series:
      dataKeys?.map((key) => {
        const value = currentData[key];
        return typeof value === "object" && value !== null
          ? value.count ?? 0
          : value ?? 0;
      }) ?? [],
    colors: breedColors,
  };

  function PieChartBox({ title, labels, series, colors }) {
    const options = {
      chart: {
        type: "donut",
      },
      labels: labels,
      legend: {
        show: false,
      },
      colors: colors,
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          return series[opts.seriesIndex];
        },
      },
      stroke: {
        width: 0,
      },
      tooltip: {
        custom: function ({ series, seriesIndex, w }) {
          const label = labels?.[seriesIndex];
          const value = series[seriesIndex];
          const currData = reqData()?.[label?.toLowerCase()];
          const vetData = reqData(true);
          const trendValue = vetData?.trend?.[label?.toLowerCase()]?.count ??
            vetData?.trend?.[label?.toLowerCase()]?.currentCount ?? 0
          const priorValue = vetData?.priorPeriod?.[label?.toLowerCase()]?.count ??
            vetData?.priorPeriod?.[label?.toLowerCase()]?.currentCount ?? 0
          const currentValue = vetData?.currentPeriod?.[label?.toLowerCase()]?.count ??
            vetData?.currentPeriod?.[label?.toLowerCase()]?.currentCount ?? 0


          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            label +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[seriesIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Count: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            value +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">% Distribution: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            currData?.percentage +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Trend Period: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            trendValue +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Prior Period: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            priorValue +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Current Period: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            currentValue +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        },
      },
    };

    return (
      <div style={chartStyle}>
        <h3>{title}</h3>
        <hr />
        <div style={{ display: "flex" }}>
          <div style={{ width: "50%" }}>
            <Chart
              options={options}
              series={series}
              type="donut"
              width="100%"
              height={250}
            />
          </div>
          <div
            style={{
              width: "50%",
              paddingLeft: "10px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {labels?.map((label, index) => {
              return (
                <div key={index} style={labelStyle}>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: colors[index],
                        display: "inline-block",
                      }}
                    ></span>
                    {label}
                  </div>
                  <div>{series[index]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const onChangeVetValue = (value) => {
    if (!value?.value) return;
    const reqvet = caseMixPerVet?.[selectedViewType]?.vets?.find(
      (vt) => vt?.doctor?.toLowerCase() === value?.value?.toLowerCase()
    );
    const reqPeriod = Object.keys(reqvet?.periods || {})?.[0];
    setFilterValues({
      ...filterValues,
      vet: value,
      period: { name: reqPeriod, value: reqPeriod },
    });
  };

  return (
    <Box>
      <div className="flex-row mb20">
        <div className="flex1-end">
          <div className="w20Per">
            <CustomSelect
              list={getPeriodList()}
              value={filterValues?.period}
              name="period"
              handleChange={(e, value) => {
                if (!value?.value) return;
                setFilterValues({ ...filterValues, period: value });
              }}
              label=""
              search
              backgroundColor="#fff"
            />
          </div>
          <div className="w20Per ml20">
            <CustomSelect
              list={periodTypeList}
              value={filterValues?.periodType}
              name="periodType"
              handleChange={(e, value) => {
                if (!value?.value) return;
                setFilterValues({ ...filterValues, periodType: value });
              }}
              label=""
              search
              backgroundColor="#fff"
            />
          </div>
          <div className="w20Per ml20">
            <CustomSelect
              list={getVetList()}
              value={filterValues?.vet}
              name="vet"
              handleChange={(e, value) => {
                onChangeVetValue(value);
              }}
              label=""
              search
              backgroundColor="#fff"
            />
          </div>
        </div>
        <Box display="flex" justifyContent="flex-end" className="ml20">
          <ToggleButtonGroup
            value={selectedViewType}
            exclusive
            onChange={(_, val) => val && setSelectedViewType(val)}
            size="small"
            sx={{
              backgroundColor: "#f0f2f5",
              borderRadius: "12px",

              // Styles for all grouped toggle buttons
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: "7px",
                borderColor: "transparent",
                color: "#333",
                transition: "none", // remove hover effect if needed

                // Only apply to selected button
                "&.Mui-selected": {
                  backgroundColor: "#005EB8",
                  color: "#fff",
                },
              },
            }}
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="quarter">Quarter</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </div>
      <PieChartBox {...breedsChart} />
    </Box>
  );
}
